// src/gestion-logistics/hooks/useJobCosting.ts
import { useState, useCallback } from "react";
import axios from "axios";
import { message } from "antd";
import API_CONFIG from "../../config/api";
import {
  JobCostingLine,
  JobCostingFormData,
  JobCostingSummary,
} from "../types/costing.types";

interface UseJobCostingResult {
  jobCostingLines: JobCostingLine[];
  summary: JobCostingSummary | null;
  loading: boolean;
  fetchJobCostingByShipment: (shipmentId: number) => Promise<void>;
  fetchJobCostingSummary: (shipmentId: number) => Promise<void>;
  createJobCostingLine: (data: JobCostingFormData) => Promise<JobCostingLine | null>;
  updateJobCostingLine: (id: number, data: Partial<JobCostingFormData>) => Promise<boolean>;
  deleteJobCostingLine: (id: number) => Promise<boolean>;
}

export const useJobCosting = (): UseJobCostingResult => {
  const [jobCostingLines, setJobCostingLines] = useState<JobCostingLine[]>([]);
  const [summary, setSummary] = useState<JobCostingSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchJobCostingByShipment = useCallback(async (shipmentId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/job-costing`,
        {
          params: { shipment_id: shipmentId },
        }
      );

      if (response.data.success) {
        setJobCostingLines(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Error fetching job costing:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement du job costing"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobCostingSummary = useCallback(async (shipmentId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/job-costing/summary/${shipmentId}`
      );

      if (response.data.success) {
        setSummary(response.data.data || null);
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Error fetching job costing summary:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement du récapitulatif"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const createJobCostingLine = useCallback(
    async (data: JobCostingFormData): Promise<JobCostingLine | null> => {
      try {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/logistics/job-costing`,
          data
        );

        if (response.data.success) {
          message.success("Ligne de costing ajoutée avec succès");
          // Refresh data for the shipment
          if (data.shipment_id) {
            await fetchJobCostingByShipment(data.shipment_id);
            await fetchJobCostingSummary(data.shipment_id);
          }
          return response.data.data;
        }
        return null;
      } catch (error: any) {
        console.error("[LOGISTICS] Error creating job costing line:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de l'ajout de la ligne de costing"
        );
        return null;
      }
    },
    [fetchJobCostingByShipment, fetchJobCostingSummary]
  );

  const updateJobCostingLine = useCallback(
    async (id: number, data: Partial<JobCostingFormData>): Promise<boolean> => {
      try {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/logistics/job-costing/${id}`,
          data
        );

        if (response.data.success) {
          message.success("Ligne de costing mise à jour avec succès");
          // Refresh data - need to find shipment_id from existing lines
          const line = jobCostingLines.find((l) => l.id === id);
          if (line?.shipment_id) {
            await fetchJobCostingByShipment(line.shipment_id);
            await fetchJobCostingSummary(line.shipment_id);
          }
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error updating job costing line:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la mise à jour de la ligne de costing"
        );
        return false;
      }
    },
    [jobCostingLines, fetchJobCostingByShipment, fetchJobCostingSummary]
  );

  const deleteJobCostingLine = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        // Get shipment_id before deletion
        const line = jobCostingLines.find((l) => l.id === id);

        const response = await axios.delete(
          `${API_CONFIG.API_URL}/logistics/job-costing/${id}`
        );

        if (response.data.success) {
          message.success("Ligne de costing supprimée avec succès");
          // Refresh data
          if (line?.shipment_id) {
            await fetchJobCostingByShipment(line.shipment_id);
            await fetchJobCostingSummary(line.shipment_id);
          }
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error deleting job costing line:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la suppression de la ligne de costing"
        );
        return false;
      }
    },
    [jobCostingLines, fetchJobCostingByShipment, fetchJobCostingSummary]
  );

  return {
    jobCostingLines,
    summary,
    loading,
    fetchJobCostingByShipment,
    fetchJobCostingSummary,
    createJobCostingLine,
    updateJobCostingLine,
    deleteJobCostingLine,
  };
};
